"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { Card } from "@/components/ui/Card";
import PublicLayout from "@/components/layout/PublicLayout";
import Link from "next/link";
import { Eye, EyeOff } from "lucide-react";

declare global {
  interface Window {
    grecaptcha: any;
  }
}

export default function LoginPage() {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    email: "",
    password: "",
  });

  useEffect(() => {
    import("@/lib/referral-tracking").then(({ initializeReferralTracking }) => {
      initializeReferralTracking();
    });

    const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
    if (siteKey && !siteKey.includes("YOUR_RECAPTCHA")) {
      const existing = document.querySelector('script[src="https://www.google.com/recaptcha/api.js"]');
      if (existing) {
        setRecaptchaReady(true);
        return;
      }
      const script = document.createElement("script");
      script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
  window.grecaptcha.ready(() => {
    setRecaptchaReady(true);
  });
};
      document.head.appendChild(script);
    } else {
      setRecaptchaReady(true);
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!form.email.trim() || !form.password.trim()) {
      setError("Please enter email and password");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
      let recaptchaToken = "";
      if (siteKey && !siteKey.includes("YOUR_RECAPTCHA")) {
        if (!window.grecaptcha) {
          setError("Security verification not ready. Please try again.");
          setLoading(false);
          return;
        }
        recaptchaToken = await window.grecaptcha.execute(siteKey, { action: "login" });
      }

      await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });
      localStorage.removeItem("garage-data");
      localStorage.removeItem("user");

      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: form.email,
          password: form.password,
          recaptchaToken,
        }),
      });

      if (!response.ok) {
        try {
          const data = await response.json();
          setError(data.error || "Login failed");
        } catch {
          setError(`Login failed with status ${response.status}`);
        }
        return;
      }

      const data = await response.json();

      console.log("[LOGIN] Response data:", {
        role: data.user.role,
        hasReferral: data.user.hasReferral,
        hasGarage: data.user.hasGarage,
        email: data.user.email,
      });

      // Store user info
      localStorage.setItem("user", JSON.stringify(data.user));
      
      // Store garage data - handle both staff and owner roles
      if (data.garage) {
        localStorage.setItem("garage-data", JSON.stringify(data.garage));
      } else if (data.user?.staff?.garage) {
        localStorage.setItem("garage-data", JSON.stringify(data.user.staff.garage));
      } else if (data.user?.garages && data.user.garages[0]) {
        const mainGarage = data.user.garages.find((garage: any) => garage?.isMainLocation) || data.user.garages[0];
        localStorage.setItem("garage-data", JSON.stringify(mainGarage));
      }
      
      // Intelligent redirect based on entry point and user roles
      if (data.user.role === "ADMIN" || data.user.role === "SUPER_ADMIN") {
        console.log("[LOGIN] Redirecting to /admin (admin role)");
        router.push("/admin");
      } else if (data.user.hasReferral) {
        // Referrers always go to referral dashboard (even if they also have a garage)
        console.log("[LOGIN] Redirecting to /referrals (has referral program)");
        router.push("/referrals");
      } else if (data.user.hasGarage) {
        // Staff go directly to jobs, owners go to dashboard
        if (data.user.role === "GARAGE_STAFF") {
          console.log("[LOGIN] Redirecting to /garage/jobs (staff)");
          router.push("/garage/jobs");
        } else {
          console.log("[LOGIN] Redirecting to /garage/dashboard (has garage)");
          router.push("/garage/dashboard");
        }
      } else {
        // Fallback: no garage or referral
        console.log("[LOGIN] Redirecting to / (fallback)");
        router.push("/");
      }
    } catch (error) {
      console.error("Login error:", error);
      setError("An error occurred during login");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicLayout>
      <div className="bg-linear-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-6 min-h-[70vh]">
        <div className="w-full max-w-md space-y-6">

          <Card className="p-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Login</h2>
            
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="your@email.com"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    autoComplete="current-password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="w-full px-4 py-3 pr-12 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute inset-y-0 right-0 px-3 text-slate-500 hover:text-slate-700"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !recaptchaReady}
                className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
              >
                {loading ? "Logging in..." : "Login"}
              </button>
            </form>

            <div className="mt-6 text-center space-y-2">
              <p className="text-sm text-slate-600">
                Don't have an account?{" "}
                <Link href="/pricing" className="text-blue-600 font-semibold hover:underline">
                  Sign up
                </Link>
              </p>
              <p className="text-xs text-slate-500">
                <Link href="/forgot-password" className="hover:underline">
                  Forgot password?
                </Link>
              </p>
            </div>
          </Card>

          <p className="text-center text-xs text-slate-400">
            By logging in, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </PublicLayout>
  );
}
